import { useMutation, useQueryClient } from 'react-query';
import isArray from 'lodash/isArray';

import { CreateItemGqlQuery, CreateItemCacheKeys } from '../../../../../types';

import { createItem } from '../../actions/createItem';

import {
  parseRequestError,
  ErrorType
} from '../../../../../utils/parseRequestError';

interface CreateQueryOptions<CreateQueryInput, CreateQueryResponse> {
  action: string;
  cacheKeys?: CreateItemCacheKeys;
  mutationFn?: (queryInput: CreateQueryInput) => Promise<CreateQueryResponse>;
  query: CreateItemGqlQuery;
  refetchTimeout?: number;
}

export interface CreateQueryContext<ItemType> {
  previousData: ItemType;
}

function useCreateQuery<
  CreateQueryInput,
  CreateQueryResponse,
  CreateQueryError extends ErrorType,
  ItemType
>({
  action,
  cacheKeys,
  mutationFn,
  query,
  refetchTimeout
}: CreateQueryOptions<CreateQueryInput, CreateQueryResponse>) {
  const queryClient = useQueryClient();

  const { data, error, isLoading, mutateAsync, reset } = useMutation<
    CreateQueryResponse,
    CreateQueryError,
    CreateQueryInput,
    CreateQueryContext<ItemType>
  >(
    mutationFn ||
      ((queryInput) =>
        createItem<CreateQueryInput, CreateQueryResponse>({
          action,
          query,
          queryInput
        })),
    {
      onSettled: (response) => {
        if (isArray(cacheKeys)) {
          const refetchByCacheKeys = () =>
            cacheKeys.map((eachCacheKey) =>
              queryClient.invalidateQueries(eachCacheKey)
            );

          refetchTimeout
            ? window?.setTimeout(refetchByCacheKeys, refetchTimeout)
            : refetchByCacheKeys();
        }
      }
    }
  );

  return {
    createQueryData: data,
    createQueryError: error,
    createQueryLoading: isLoading,
    createQueryErrorMessage: parseRequestError(error),
    createQuery: mutateAsync,
    createQueryReset: reset
  };
}

export default useCreateQuery;
